/**
 * Human-readable, translation-ready labels for enum codes.
 *
 * The enum CODE (e.g. `WRITTEN_ADVICE`) is the stable key that lives in the data and the OAS
 * contract; the UI never shows the raw code. This module maps code -> a localized label so:
 *  - the UI reads cleanly (no ALL_CAPS_WITH_UNDERSCORES), and
 *  - turning on translation later is a lookup, not string-munging: add the label for a language
 *    and everything updates. (Arabic entries currently mirror English; a translator fills them
 *    in without touching any component.)
 *
 * Prefer `enumLabel(kind, code, lang)` over ad-hoc `code.replace(/_/g, ' ')`.
 */
import type { Lang } from './language';

type LabelMap = Record<string, string>;

const CASE_STATUS: LabelMap = {
  DRAFT: 'Draft',
  SUBMITTED: 'Submitted',
  TRIAGE: 'In triage',
  ASSIGNED: 'Assigned',
  UNDER_REVIEW: 'Under review',
  WAITING_FOR_LOCAL_DOCTOR: 'Waiting for local doctor',
  VIDEO_REQUIRED: 'Video consultation required',
  SPECIALIST_FACE_TO_FACE: 'Face-to-face specialist required',
  RECOMMENDATION_PROVIDED: 'Recommendation provided',
  LOCAL_FOLLOW_UP: 'Local follow-up',
  CLOSED: 'Closed',
};

const PRIORITY: LabelMap = {
  LOW: 'Low',
  MEDIUM: 'Medium',
  HIGH: 'High',
  URGENT: 'Urgent',
};

const FUNDING_STATUS: LabelMap = {
  SELF_FUNDED: 'Self-funded',
  CHARITY_FUNDED: 'Charity-funded',
  PENDING: 'Pending',
};

const RECOMMENDATION_OUTCOME: LabelMap = {
  WRITTEN_ADVICE: 'Written advice',
  FURTHER_INFORMATION_REQUIRED: 'Further information required',
  VIDEO_CONSULTATION_REQUIRED: 'Video consultation required',
  FACE_TO_FACE_SPECIALIST_REQUIRED: 'Face-to-face specialist required',
  NO_FURTHER_ACTION_REQUIRED: 'No further action required',
  OTHER: 'Other',
};

const SEX: LabelMap = {
  MALE: 'Male',
  FEMALE: 'Female',
  OTHER: 'Other',
  UNKNOWN: 'Unknown',
};

const DOCUMENT_TYPE: LabelMap = {
  BLOOD_RESULT: 'Blood result',
  SCAN: 'Scan',
  XRAY: 'X-ray',
  MRI: 'MRI',
  CT: 'CT',
  MEDICAL_REPORT: 'Medical report',
  PHOTOGRAPH: 'Photograph',
  OTHER: 'Other',
};

const DECISION_TYPE: LabelMap = {
  ASSIGNED: 'Assigned',
  REASSIGNED: 'Reassigned',
  SPECIALTY_SET: 'Specialty set',
  INFO_REQUESTED: 'Information requested',
  RECOMMENDATION_MADE: 'Recommendation made',
  ESCALATED: 'Escalated',
  CLOSED: 'Closed',
  OTHER: 'Update',
};

/** English label dictionaries by enum kind. */
const EN = {
  caseStatus: CASE_STATUS,
  priority: PRIORITY,
  fundingStatus: FUNDING_STATUS,
  recommendationOutcome: RECOMMENDATION_OUTCOME,
  documentType: DOCUMENT_TYPE,
  decisionType: DECISION_TYPE,
  sex: SEX,
} as const;

export type EnumKind = keyof typeof EN;

/**
 * Per-language dictionaries. Arabic currently mirrors English as a placeholder so the wiring is
 * complete; a translator can replace these without touching components. Codes not yet translated
 * fall back to English, then to a de-underscored code as a last resort.
 */
const DICTS: Record<Lang, typeof EN> = {
  en: EN,
  ar: EN, // TODO(i18n): supply Arabic labels; structure is ready.
};

/** Fallback so we never surface a raw ALL_CAPS code even for an unknown value. */
function humanize(code: string): string {
  const lower = code.replace(/_/g, ' ').toLowerCase();
  return lower.charAt(0).toUpperCase() + lower.slice(1);
}

export function enumLabel(kind: EnumKind, code: string | null | undefined, lang: Lang): string {
  if (!code) return '';
  return DICTS[lang][kind][code] ?? EN[kind][code] ?? humanize(code);
}
